/**
 * Copyright 2021-2023 Highway9 Networks Inc.
 */
import { Action, AnyAction, Dispatch, MiddlewareAPI, configureStore } from "@reduxjs/toolkit";
import tenantSlice from "./context/tenantSlice";
import serviceInstanceSlice from "./context/serviceInstanceSlice";
import partnerSlice from "./context/partnerSlice";
import orderSlice from "./context/orderSlice";
import orderCreateSlice from "./context/orderCreateSlice";
import hardwareSlice from "./context/hardwareSlice";
import loadingSlice from "./context/loadingSlice";
import utilitySlice from "./context/utilitySlice";
import productSlice from "./context/productSlice";
import deviceManagementSlice from "./context/deviceManagementSlice";
import userSlice from "./context/userSlice";
import rolesSlice from "./context/rolesSlice";
import certificateSlice from "./context/mocngwCertificateSlice";
import licenseSlice from "./context/licenseSlice";
import serviceKpiSlice from "./context/serviceKpiSlice";
import ipsecCertMetricsSlice from "./context/ipsecCertMetricsSlice";
import aiAssistantSlice from "./context/aiAssistantSlice";
import settingsSlice from "./context/settingsSlice";

export const store = configureStore({
  reducer: {
    tenant: tenantSlice,
    serviceInstance: serviceInstanceSlice,
    partner: partnerSlice,
    hardware: hardwareSlice,
    loading: loadingSlice,
    utility: utilitySlice,
    product: productSlice,
    devices: deviceManagementSlice,
    user: userSlice,
    roles: rolesSlice,
    certificate: certificateSlice,
    license: licenseSlice,
    serviceKpi: serviceKpiSlice,
    ipsecCertMetrics: ipsecCertMetricsSlice,
    order: orderSlice,
    orderCreate: orderCreateSlice,
    aiAssistant: aiAssistantSlice,
    settings: settingsSlice,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware(),
  // Production builds default this to true, so any user with the Redux DevTools
  // extension has every dispatch's full payload and a state snapshot retained by
  // the extension (50 by default) - at fleet scale that alone can exhaust a tab (MCS-4316)
  devTools: import.meta.env.DEV,
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export type AppThunk<ReturnType = void> = Action | ((dispatch: AppDispatch, getState: () => RootState) => ReturnType);
// Redux Middleware
export interface ReduxMiddleware<
  S = any, // type of the state supported by this middleware.
  D extends Dispatch = Dispatch, // type of the dispatch supported by this middleware.
  A extends Action = AnyAction, // type of the action(s) supported by this middleware, inferred from actions passed to applyMiddleware
  DispatchExt = {} // optional override return behavior of dispatch
> {
  (api: MiddlewareAPI<D, S>): (next: D) => (action: A) => A | DispatchExt;
}
